import { ErrorNote, Tile, errorMessage } from '@/components/ui';
import { formatDate, formatMoney } from '@/format';
import { useAgeing, useOverview } from '@/hooks/finance';
import { Table } from '@/components/ui';

/**
 * Financials › Dashboard.
 *
 * Six figures and an ageing table, in the words a person uses rather than an
 * accountant's: "Cash in hand", "Still to receive", not "Debtors control".
 *
 * The "To be classified" card is not decoration. An item category nobody has
 * mapped posts to suspense rather than refusing a storekeeper's receipt, and
 * without somewhere visible to show that balance the escape hatch quietly becomes
 * the habit. A non-zero figure here also blocks a month from closing.
 */
export function FinanceDashboard() {
  const overview = useOverview();
  const ageing = useAgeing('');

  if (overview.error) {
    return <ErrorNote message={errorMessage(overview.error, 'The figures could not be read')} />;
  }

  const o = overview.data;
  const suspense = Number(o?.toBeClassified ?? 0);

  return (
    <div className="stack">
      <div className="tiles">
        <Tile
          label="Cash in hand"
          value={formatMoney(o?.cashInHand ?? 0)}
          note="Across every cash box you can see"
        />
        <Tile
          label="In the bank"
          value={formatMoney(o?.inTheBank ?? 0)}
          note="Bank accounts and wallets"
        />
        <Tile
          label="Still to receive"
          value={formatMoney(o?.stillToReceive ?? 0)}
          note="Owed by buyers on posted bills"
        />
        <Tile
          label="Still to pay"
          value={formatMoney(o?.stillToPay ?? 0)}
          note="Suppliers, wages and other payables"
        />
        <Tile
          label="Sales this month"
          value={formatMoney(o?.salesThisPeriod ?? 0)}
          note={o ? `${formatDate(o.from)} to ${formatDate(o.to)}` : ''}
        />
        <Tile
          label="Costs this month"
          value={formatMoney(o?.costsThisPeriod ?? 0)}
          note={o ? `${formatDate(o.from)} to ${formatDate(o.to)}` : ''}
        />
        <Tile
          label="To be classified"
          value={formatMoney(o?.toBeClassified ?? 0)}
          note={
            suspense === 0
              ? 'Nothing waiting — every item category is mapped'
              : 'Map the item categories these came from, then re-post those documents'
          }
          // Warned about rather than shown plainly: anything sitting here stops a
          // month closing, so it is a thing to go and fix.
          tone={suspense === 0 ? undefined : 'warn'}
        />
      </div>

      <section className="panel">
        <h2>Who owes what</h2>
        <p className="muted">
          By how old the bill is, not how old the balance is — ageing is about which
          document is still outstanding.
        </p>
        <Table
          count={ageing.data?.items.length ?? 0}
          empty="Nobody owes anything."
          head={
            <tr>
              <th>Buyer</th>
              <th className="num">This week</th>
              <th className="num">Up to a month</th>
              <th className="num">One to two months</th>
              <th className="num">Older</th>
              <th className="num">Total</th>
            </tr>
          }
        >
          {(ageing.data?.items ?? []).map((row) => (
            <tr key={row.buyerId}>
              <td>{row.buyerName}</td>
              <td className="num">{formatMoney(row.week)}</td>
              <td className="num">{formatMoney(row.month)}</td>
              <td className="num">{formatMoney(row.twoMonths)}</td>
              <td className="num">{formatMoney(row.older)}</td>
              <td className="num">
                <strong>{formatMoney(row.closingBalance)}</strong>
              </td>
            </tr>
          ))}
        </Table>
      </section>
    </div>
  );
}
